import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

type SetNode = (node: ReactNode) => void;

// Two contexts rather than one `{ node, setNode }` object — see the matching
// comment in `page-title-slot.tsx` for why: bundling them made the publisher
// (`setup.tsx`) a subscriber of the very value it was writing, which is an
// infinite render loop (each publish forces the publisher to re-render,
// producing a new `node` reference, re-firing the publish effect, forever).
// `setNode` from `useState` is referentially stable, so a context carrying
// only that never changes and the publisher is never forced to re-render by
// its own writes.
const NO_PROVIDER = Symbol('secondary-sidebar-slot-no-provider');
const SecondarySidebarSlotValueContext = createContext<ReactNode | typeof NO_PROVIDER>(NO_PROVIDER);
const SecondarySidebarSlotSetterContext = createContext<SetNode | null>(null);

/**
 * Lets a layout route (today: Setup) publish a second, full-height nav
 * column that sits between the main sidebar and the page content — the
 * `/setup` sub-nav (Company / Activities / Rate cards / …).
 *
 * The column has to render as a sibling of the page content inside
 * `SidebarInset`, NOT nested inside the `<Outlet>` — content there is offset
 * by `mt-16` to clear the fixed `AppHeader`, and `SidebarInset` itself is
 * `overflow-hidden`, so anything inside the outlet that tried to reach back
 * up to the true top of the viewport would just get clipped. Publishing
 * through this slot instead means `AppLayout` renders the column outside
 * that offset, full height, and `AppHeader` (which reads the same slot) can
 * shift its own `left` edge past it — exactly how the main sidebar already
 * sits clear of the header.
 */
export function SecondarySidebarSlotProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<ReactNode>(null);
  return (
    <SecondarySidebarSlotSetterContext.Provider value={setNode}>
      <SecondarySidebarSlotValueContext.Provider value={node}>
        {children}
      </SecondarySidebarSlotValueContext.Provider>
    </SecondarySidebarSlotSetterContext.Provider>
  );
}

/** Read the current slot content. Used by `AppLayout` and `AppHeader` only. */
export function useSecondarySidebarSlotValue(): ReactNode {
  const value = useContext(SecondarySidebarSlotValueContext);
  if (value === NO_PROVIDER) {
    throw new Error(
      'useSecondarySidebarSlotValue must be used within SecondarySidebarSlotProvider',
    );
  }
  return value;
}

/**
 * Publish `node` as the secondary sidebar column for as long as the calling
 * component stays mounted; clears itself on unmount so leaving `/setup`
 * (or wherever else this is used) collapses the column and the header's
 * offset shrinks back automatically.
 */
export function useSecondarySidebarSlot(node: ReactNode): void {
  const setNode = useContext(SecondarySidebarSlotSetterContext);
  if (!setNode) {
    throw new Error('useSecondarySidebarSlot must be used within SecondarySidebarSlotProvider');
  }
  useEffect(() => {
    setNode(node);
    return () => setNode(null);
  }, [node, setNode]);
}
